/**
 * SymbolStore v2 — namespace-isolated state storage.
 * Simplified from v1: no namespace aliases (pipeline's job).
 */

type Listener = (value: unknown) => void

const UNSAFE_STATE_PATH_SEGMENTS = new Set([
  "__proto__",
  "prototype",
  "constructor",
])

function getEnumerableDataProperty(
  value: object,
  key: PropertyKey,
): PropertyDescriptor | undefined {
  const descriptor = Object.getOwnPropertyDescriptor(value, key)
  return descriptor?.enumerable === true && "value" in descriptor
    ? descriptor
    : undefined
}

function isEnumerableDataProperty(value: object, key: PropertyKey): boolean {
  return getEnumerableDataProperty(value, key) !== undefined
}

function isOrdinaryObjectPrototype(value: object): boolean {
  if (Object.getPrototypeOf(value) !== null) return false
  const constructorDescriptor = Object.getOwnPropertyDescriptor(
    value,
    "constructor",
  )
  if (
    !constructorDescriptor ||
    !("value" in constructorDescriptor) ||
    typeof constructorDescriptor.value !== "function"
  ) {
    return false
  }
  return Object.getOwnPropertyDescriptor(
    constructorDescriptor.value,
    "prototype",
  )?.value === value
}

function hasDataOnlyObjectChain(
  value: object,
  seen: Set<object>,
): boolean {
  if (seen.has(value)) return false
  seen.add(value)
  if (
    !Reflect.ownKeys(value).every(
      (key) =>
        typeof key === "string" && isEnumerableDataProperty(value, key),
    )
  ) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  if (prototype === null || isOrdinaryObjectPrototype(prototype)) return true
  return hasDataOnlyObjectChain(prototype, seen)
}

/**
 * Nested state traversal is limited to JSON-like data containers. The shape
 * check is realm-independent and rejects intrinsic prototypes, accessors, and
 * branded host objects without maintaining a constructor/prototype denylist.
 */
function isTraversableStateContainer(value: object): boolean {
  try {
    if (Array.isArray(value)) {
      return Reflect.ownKeys(value).every((key) =>
        key === "length" ||
        (typeof key === "string" &&
          /^(0|[1-9]\d*)$/.test(key) &&
          isEnumerableDataProperty(value, key)),
      )
    }

    return hasDataOnlyObjectChain(value, new Set())
  } catch {
    return false
  }
}

export class SymbolStore {
  private data: Record<string, unknown> = {}
  /**
   * Positive validation cache. Traversal still reads the selected own data
   * descriptor on every hop, so a later accessor/non-enumerable replacement
   * cannot execute or inherit trust from this set.
   */
  private traversableStateContainers = new WeakSet<object>()
  private listeners = new Map<string, Set<Listener>>()
  /** Registered namespace aliases: when get("ns.key") misses, try get("key") */
  private namespaceAliases = new Set<string>()

  // Wave 9A — legacy expression prefix read aliases. Runtime tolerates
  // legacy corpus on read; all writes normalize to canonical via the
  // existing @ ↔ localState sync layer in state-manager.ts.
  // Gated by COGO_RUNTIME_LEGACY_PREFIX_ALIASES (default ON).
  private readonly LEGACY_READ_ALIAS: Record<string, string> = {
    localState: "local",
    state: "local",
  }

  constructor() {
    this.traversableStateContainers.add(this.data)
  }

  /** Classify a variable path into its tier. */
  static classifyVariable(path: string): "domain" | "local" | "system" {
    if (path.startsWith("@")) return "local"
    if (path.startsWith("_")) return "system"
    return "domain"
  }

  /**
   * Register a namespace alias so that get("ns.key") falls back to get("key")
   * when the namespaced path doesn't exist. This bridges the gap between
   * flat state keys (used by flows) and namespaced references (used by UI JSON).
   */
  registerNamespaceAlias(namespace: string): void {
    this.namespaceAliases.add(namespace)
  }

  set(path: string, value: unknown): void {
    this.writeWithAlias(path, value)
    for (const [key, fns] of this.listeners) {
      if (key === "" || path === key || path.startsWith(key + ".") || key.startsWith(path + ".")) {
        const val = key === "" ? undefined : this.get(key)
        fns.forEach((fn) => fn(val))
      }
    }
  }

  /** Set value WITHOUT notifying listeners — prevents computed->setState infinite loops. */
  setQuiet(path: string, value: unknown): void {
    this.writeWithAlias(path, value)
  }

  /** Replace one runtime-owned namespace without applying user-state aliases. */
  replaceNamespaceQuiet(namespace: string, values: Record<string, unknown>): void {
    if (namespace.includes(".")) throw new Error("Namespace must be a single safe segment")
    this.splitPath(namespace)
    this.setNestedValue(this.data, [namespace], {})
    for (const [key, value] of Object.entries(values)) {
      this.setNestedValue(this.data, [namespace, ...this.splitPath(key)], structuredClone(value))
    }
  }

  /** Write value + bidirectional alias. Single source of truth for both set() and setQuiet(). */
  private writeWithAlias(path: string, value: unknown): void {
    this.setNestedValue(this.data, this.splitPath(path), value)
    const dotIdx = path.indexOf(".")
    if (dotIdx > 0) {
      const ns = path.slice(0, dotIdx)
      if (this.namespaceAliases.has(ns)) {
        // Forward: `ns.key` → flat `key` (e.g. local.count → count) for registered namespace aliases.
        const flatPath = path.slice(dotIdx + 1)
        this.setNestedValue(this.data, this.splitPath(flatPath), value)
      }
    } else if (dotIdx < 0 && !path.startsWith("@") && !path.startsWith("_") && this.namespaceAliases.has("local")) {
      // Backward: a BARE local-variable write (the validator-required setState target form, e.g.
      // setState("count")) is mirrored under `local.<key>` so the canonical `#{local.<key>}` read sees the
      // same value. Without this, a bare write lands on the flat key while `#{local.X}` reads a separate
      // namespace and the UI never updates (the L3 dead-display bug).
      this.setNestedValue(this.data, ["local", ...this.splitPath(path)], value)
    }
  }

  get(path: string): unknown {
    if (path.startsWith("@ds:")) {
      const tokenName = path.slice(4)
      const tokenKeys = this.splitPath(tokenName)
      const dsVal = this.getNestedValue(this.data, ["ds", ...tokenKeys]) ??
                    this.getNestedValue(this.data, ["@ds", ...tokenKeys]) ??
                    this.getNestedValue(this.data, tokenKeys)
      if (dsVal !== undefined) return dsVal
    }

    const keys = this.splitPath(path)
    const val = this.getNestedValue(this.data, keys)
    if (val !== undefined) return val

    // 5-tier Staleness resolution order fallback:
    // Resolve namespaces in order of Staleness: @ds: ➔ __i18n ➔ __symbol ➔ __app ➔ __bind
    const explicitPrefixes = [
      "@ds:", "ds.", "@ds.", "__i18n.", "__symbol.", "__app.", "__bind.", "local.", "@", "_"
    ]
    const hasExplicitPrefix = explicitPrefixes.some(p => path.startsWith(p))

    if (!hasExplicitPrefix) {
      const dsVal = this.getNestedValue(this.data, ["ds", ...keys]) ??
                    this.getNestedValue(this.data, ["@ds", ...keys])
      if (dsVal !== undefined) return dsVal

      const i18nVal = this.getNestedValue(this.data, ["__i18n", ...keys])
      if (i18nVal !== undefined) return i18nVal

      const symbolVal = this.getNestedValue(this.data, ["__symbol", ...keys])
      if (symbolVal !== undefined) return symbolVal

      const appVal = this.getNestedValue(this.data, ["__app", ...keys])
      if (appVal !== undefined) return appVal

      const bindVal = this.getNestedValue(this.data, ["__bind", ...keys])
      if (bindVal !== undefined) return bindVal
    }

    // Namespace alias fallback: "paymentHistory.dateType" → try "dateType"
    if (keys.length > 1 && this.namespaceAliases.has(keys[0])) {
      const aliased = this.getNestedValue(this.data, keys.slice(1))
      if (aliased !== undefined) return aliased
    }
    // Wave 9A — legacy expression prefix aliases (read-only rewrite, default ON).
    // process is undefined in browser; guard so default-ON behavior is preserved.
    const aliasesEnabled =
      typeof process === "undefined" ||
      process.env?.["COGO_RUNTIME_LEGACY_PREFIX_ALIASES"] !== "0"
    if (aliasesEnabled && keys.length > 1) {
      const ns = keys[0]
      // localState.X / state.X → local.X
      const canonical = this.LEGACY_READ_ALIAS[ns]
      if (canonical !== undefined) {
        return this.getNestedValue(this.data, [canonical, ...keys.slice(1)])
      }
      // @X → local.X (written as "@name" key — try "local.<rest>" path)
      // This handles `get("local.name")` when data was written via `set("@name", v)`
      // by checking whether `local.<key>` can be found if `@<key>` is written instead.
      // Actually @-prefixed writes go to `@name` as-is; local.name would need direct write.
      // Wave 9A adds: if path starts with "local.", fall back to "@<rest>"
      if (ns === "local") {
        const atPath = `@${keys.slice(1).join(".")}`
        return this.getNestedValue(this.data, this.splitPath(atPath))
      }
      // Wave 9A — response.X → _results.__last.X (restApi result shortcut)
      if (ns === "response") {
        return this.getNestedValue(this.data, ["_results", "__last", ...keys.slice(1)])
      }
      // Wave 9A — ui.X → appData.X
      if (ns === "ui") {
        return this.getNestedValue(this.data, ["appData", ...keys.slice(1)])
      }
    }
    return undefined
  }

  resolve(template: string): unknown {
    if (typeof template !== "string") return template

    const pureMatch = /^#\{([^}]+)\}$/.exec(template)
    if (pureMatch) {
      const expr = pureMatch[1].trim()
      if (expr.startsWith("env.vault.") || expr.startsWith("props.vault.")) {
        return template
      }
      if (expr.startsWith("!")) {
        return !this.get(expr.slice(1))
      }
      const ternaryResult = evaluateTernary(expr, (p) => this.resolveValue(p))
      if (ternaryResult !== undefined) return ternaryResult
      const orIdx = findTopLevelLogical(expr, "||")
      if (orIdx >= 0) {
        const left = this.resolve(`#{${expr.slice(0, orIdx).trim()}}`)
        if (left) return left
        return this.resolve(`#{${expr.slice(orIdx + 2).trim()}}`)
      }
      const andIdx = findTopLevelLogical(expr, "&&")
      if (andIdx >= 0) {
        const left = this.resolve(`#{${expr.slice(0, andIdx).trim()}}`)
        if (!left) return left
        return this.resolve(`#{${expr.slice(andIdx + 2).trim()}}`)
      }
      const compMatch = /^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/.exec(expr)
      if (compMatch) {
        const left = this.resolveValue(compMatch[1].trim())
        const op = compMatch[2]
        const right = this.resolveValue(compMatch[3].trim())
        switch (op) {
          case "===": case "==": return left === right
          case "!==": case "!=": return left !== right
          case ">": return (left as number) > (right as number)
          case "<": return (left as number) < (right as number)
          case ">=": return (left as number) >= (right as number)
          case "<=": return (left as number) <= (right as number)
        }
      }
      return this.get(expr)
    }

    const localMatch = /^@\{([^}]+)\}$/.exec(template)
    if (localMatch) {
      return this.get(`@${localMatch[1].trim()}`)
    }

    if (template.includes("#{")) {
      return template.replace(/#\{([^}]+)\}/g, (_, ref) => {
        const expr = (ref as string).trim()
        if (expr.startsWith("env.vault.") || expr.startsWith("props.vault.")) {
          return `#{${expr}}`
        }
        const tv = evaluateTernary(expr, (p) => this.resolveValue(p))
        if (tv !== undefined) return tv != null ? String(tv) : ""
        const val = this.get(expr)
        return (val != null && typeof val !== "object") ? String(val) : ""
      })
    }

    return template
  }

  subscribe(path: string, listener: Listener): () => void {
    if (!this.listeners.has(path)) {
      this.listeners.set(path, new Set())
    }
    this.listeners.get(path)!.add(listener)
    return () => this.listeners.get(path)?.delete(listener)
  }

  getSnapshot(): Record<string, unknown> {
    return { ...this.data }
  }

  toContext(): Record<string, unknown> {
    return { ...this.data }
  }

  private splitPath(path: string): string[] {
    const keys = !path.includes("[")
      ? path.split(".")
      : path.replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean)
    for (const key of keys) {
      if (UNSAFE_STATE_PATH_SEGMENTS.has(key)) {
        throw new Error(`Unsafe state path segment: ${key}`)
      }
    }
    return keys
  }

  private resolveValue(expr: string): unknown {
    return resolveLiteral(expr, (p) => this.get(p))
  }

  private canTraverseStateContainer(value: object): boolean {
    if (this.traversableStateContainers.has(value)) return true
    if (!isTraversableStateContainer(value)) return false
    this.traversableStateContainers.add(value)
    return true
  }

  private rememberTraversableStateValue(value: unknown): void {
    if (
      value !== null &&
      typeof value === "object" &&
      !this.traversableStateContainers.has(value) &&
      isTraversableStateContainer(value)
    ) {
      this.traversableStateContainers.add(value)
    }
  }

  private createStateContainer(nextKey: string): Record<string, unknown> {
    const container: Record<string, unknown> = /^(0|[1-9]\d*)$/.test(nextKey)
      ? []
      : Object.create(null)
    this.traversableStateContainers.add(container)
    return container
  }

  private writeOwnStateValue(
    container: Record<string, unknown>,
    key: string,
    value: unknown,
  ): void {
    const existing = Object.getOwnPropertyDescriptor(container, key)
    if (existing && !existing.configurable) {
      if (
        "value" in existing &&
        existing.writable === true &&
        (existing.enumerable === true ||
          (Array.isArray(container) && key === "length"))
      ) {
        Object.defineProperty(container, key, { ...existing, value })
        return
      }
      throw new Error(`Unsafe or non-writable state property: ${key}`)
    }
    Object.defineProperty(container, key, {
      configurable: true,
      enumerable: true,
      value,
      writable: true,
    })
  }

  private getNestedValue(obj: unknown, keys: string[]): unknown {
    let current = obj
    for (const key of keys) {
      if (current == null || typeof current !== "object") return undefined
      if (!this.canTraverseStateContainer(current)) return undefined
      const property = Array.isArray(current) && key === "length"
        ? Object.getOwnPropertyDescriptor(current, key)
        : getEnumerableDataProperty(current, key)
      if (!property) return undefined
      if (!("value" in property)) return undefined
      current = property.value
    }
    return current
  }

  private setNestedValue(obj: Record<string, unknown>, keys: string[], value: unknown): void {
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      let nextValue = getEnumerableDataProperty(obj, key)?.value
      if (
        nextValue === null ||
        typeof nextValue !== "object" ||
        !this.canTraverseStateContainer(nextValue)
      ) {
        // A missing intermediate container whose NEXT segment is a numeric
        // index is a collection write (e.g. set("appData.members.0.email"))
        // — create an ARRAY so Array.isArray consumers (live-bound
        // data-tables, $-array accessors) keep working. Creating {} here
        // produced {"0":{...}} and silently broke every array consumer of
        // the collection (2026-07-22 commercial WebMCP canary bug). Existing
        // JSON-like data containers are preserved; opaque/shared objects are
        // replaced before traversal so state writes cannot mutate them.
        const container = this.createStateContainer(keys[i + 1])
        this.writeOwnStateValue(obj, key, container)
        nextValue = container
      }
      obj = nextValue as Record<string, unknown>
    }
    this.writeOwnStateValue(obj, keys[keys.length - 1], value)
    this.rememberTraversableStateValue(value)
  }
}

// ---- Expression evaluation utilities ----

export function evaluateTernary(
  expr: string,
  resolve: (path: string) => unknown,
): unknown | undefined {
  const m = /^(.+?)\s*\?\s*(?:'([^']*)'|"([^"]*)")\s*:\s*(.+)$/.exec(expr)
  if (!m) return undefined
  const condition = m[1].trim()
  const trueVal = m[2] ?? m[3]
  const falseExpr = m[4].trim()
  const result = evaluateCondition(condition, resolve)
  if (result) return trueVal
  if (/^'[^']*'$/.test(falseExpr)) return falseExpr.slice(1, -1)
  if (/^"[^"]*"$/.test(falseExpr)) return falseExpr.slice(1, -1)
  const nested = evaluateTernary(falseExpr, resolve)
  if (nested !== undefined) return nested
  return undefined
}

export function evaluateCondition(
  expr: string,
  resolve: (path: string) => unknown,
): boolean {
  const orIdx = findTopLevelLogical(expr, "||")
  if (orIdx >= 0) {
    return evaluateCondition(expr.slice(0, orIdx).trim(), resolve) ||
           evaluateCondition(expr.slice(orIdx + 2).trim(), resolve)
  }
  const andIdx = findTopLevelLogical(expr, "&&")
  if (andIdx >= 0) {
    return evaluateCondition(expr.slice(0, andIdx).trim(), resolve) &&
           evaluateCondition(expr.slice(andIdx + 2).trim(), resolve)
  }
  const m = /^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/.exec(expr)
  if (m) {
    const left = resolveLiteral(m[1].trim(), resolve)
    const op = m[2]
    const right = resolveLiteral(m[3].trim(), resolve)
    switch (op) {
      case "===": case "==": return left === right
      case "!==": case "!=": return left !== right
      case ">": return (left as number) > (right as number)
      case "<": return (left as number) < (right as number)
      case ">=": return (left as number) >= (right as number)
      case "<=": return (left as number) <= (right as number)
    }
  }
  if (expr.startsWith("!")) return !resolve(expr.slice(1))
  return !!resolve(expr)
}

function findTopLevelLogical(expr: string, op: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i < expr.length - (op.length - 1); i++) {
    const ch = expr[i]
    if (ch === "'" && !inDouble) inSingle = !inSingle
    else if (ch === '"' && !inSingle) inDouble = !inDouble
    else if (!inSingle && !inDouble) {
      if (ch === "(") depth++
      else if (ch === ")") depth--
      else if (depth === 0 && expr.slice(i, i + op.length) === op) {
        return i
      }
    }
  }
  return -1
}

export function resolveLiteral(
  expr: string,
  resolve: (path: string) => unknown,
): unknown {
  if (/^'[^']*'$/.test(expr)) return expr.slice(1, -1)
  if (/^"[^"]*"$/.test(expr)) return expr.slice(1, -1)
  if (/^-?\d+(\.\d+)?$/.test(expr)) return Number(expr)
  if (expr === "true") return true
  if (expr === "false") return false
  if (expr === "null" || expr === "undefined") return null
  const arithMatch = /^(.+?)\s*([+-])\s*(\d+(?:\.\d+)?)$/.exec(expr)
  if (arithMatch) {
    const pathVal = Number(resolve(arithMatch[1].trim()) ?? 0)
    const num = Number(arithMatch[3])
    return arithMatch[2] === "+" ? pathVal + num : pathVal - num
  }
  return resolve(expr)
}
